import { RefObject, useEffect, useState } from 'react';

/**
 * Custom hook that returns the width and height of a DOM element and handles resize events
 * @param ref - React ref object pointing to the DOM element
 * @returns An object containing the current width and height of the element in pixels
 */
export default function useElementSize<T extends HTMLElement>(
  ref: RefObject<T | null>,
): { width: number; height: number } {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Set initial size
    setSize({
      width: element.offsetWidth,
      height: element.offsetHeight,
    });

    // Create ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Use borderBoxSize for more accurate measurements
        const newWidth =
          entry.borderBoxSize?.[0]?.inlineSize ||
          entry.target.getBoundingClientRect().width;
        const newHeight =
          entry.borderBoxSize?.[0]?.blockSize ||
          entry.target.getBoundingClientRect().height;
        setSize({ width: newWidth, height: newHeight });
      }
    });

    // Start observing
    resizeObserver.observe(element);

    // Cleanup function
    return () => {
      resizeObserver.disconnect();
    };
  }, [ref.current]);

  return size;
}
